import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Shell } from '../components/Shell';
import { parseSessionInput } from '../net/sessionLink';

export function LandingPage() {
  const navigate = useNavigate();
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <Shell>
      <section className="hero">
        <h1>Hear the mix, wherever you are.</h1>
        <p className="lede">
          AuxCast streams the audio out of your DAW straight into someone&rsquo;s browser. No
          uploads, no bounce, no account — you send a link and they press play. The relay is yours
          to self-host, so the audio never touches anyone else&rsquo;s server.
        </p>

        <form
          className="join-form"
          onSubmit={(event) => {
            event.preventDefault();
            const id = parseSessionInput(value);
            if (!id) {
              setError('That does not look like a session code or link.');
              return;
            }
            setError(null);
            void navigate(`/s/${id}`);
          }}
        >
          <label className="visually-hidden" htmlFor={inputId}>
            Session code or link
          </label>
          <input
            id={inputId}
            type="text"
            value={value}
            placeholder="Paste a link, or type the code"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            onChange={(event) => {
              setValue(event.target.value);
              if (error) setError(null);
            }}
            aria-invalid={error !== null}
            aria-describedby={error ? errorId : undefined}
          />
          <button type="submit" className="btn btn-primary">
            Listen
          </button>
        </form>
        <p className="field-error" id={errorId} role="alert">
          {error}
        </p>
      </section>

      <section className="how" aria-labelledby="how-heading">
        <h2 id="how-heading">Streaming from your DAW</h2>
        <ol>
          <li>
            <h3>Load the plugin</h3>
            <p>
              Drop the AuxCast VST3 or AU on the track or bus you want people to hear — usually the
              stereo output.
            </p>
          </li>
          <li>
            <h3>Point it at your relay</h3>
            <p>
              Enter your relay&rsquo;s address and API key. Keys are issued on the server with{' '}
              <code>pnpm keys:add</code>.
            </p>
          </li>
          <li>
            <h3>Hit connect</h3>
            <p>
              The plugin encodes to Opus and hands you a link. Add a passcode first if the session
              should not be open to anyone holding it.
            </p>
          </li>
          <li>
            <h3>Share the link</h3>
            <p>
              Listeners can open it before you start playing — they simply wait, and the audio
              begins the moment you do.
            </p>
          </li>
        </ol>
        <p className="footnote">
          Playback needs a modern browser with AudioWorklet support: Chrome, Edge, Firefox and
          Safari 14.1 or newer all qualify.
        </p>
      </section>
    </Shell>
  );
}
